import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import useSWR from 'swr';

import { buildFilterParams } from '~/components/compliance/buildFilterParams';
import { compliancesDefectAnalyticsRetrieve } from '~/data/generated/endpoints/compliances/compliances';
import type { CompliancesDefectAnalyticsRetrieveParams } from '~/data/generated/model';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type API_DefectAnalytics, api_defectAnalyticsSchema } from '~/data/schemas/compliance';

/**
 * SWR key prefix for the defect-analytics request. Exported so the compliance
 * screen can invalidate it alongside the table after an in-app mutation: the
 * analytics live under their own key, so the table's refresh doesn't reach
 * them, and with revalidation otherwise off they would keep pre-change numbers
 * while the tiles beside them updated. (CX-416)
 */
export const DEFECT_ANALYTICS_SWR_KEY = 'compliance-defect-analytics';

type DefectAnalyticsFetcher = {
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  searchParams: URLSearchParams;
};

/**
 * The query the aggregate actually receives.
 *
 * Built from the same helper the table uses, so the charts describe exactly the
 * rows the grid is showing and can't drift from the tiles beside them.
 * `limit`/`offset`/`ordering` are paging and row-ordering concerns that an
 * aggregate ignores, so they are dropped. The SWR key is then derived from this
 * result rather than from the raw inputs — otherwise re-sorting the table would
 * change the key and refetch a payload guaranteed to be identical.
 */
export function buildDefectAnalyticsParams(
  params: DefectAnalyticsFetcher,
): CompliancesDefectAnalyticsRetrieveParams {
  // buildFilterParams already folds quickFilterValues into `search`, so there is
  // nothing to add here beyond dropping the paging/ordering keys.
  const {
    limit: _limit,
    offset: _offset,
    ordering: _ordering,
    ...filterParams
  } = buildFilterParams({ ...params, page: 0, pageSize: 0 });

  return filterParams as CompliancesDefectAnalyticsRetrieveParams;
}

const defectAnalyticsFetcher = async (
  requestParams: CompliancesDefectAnalyticsRetrieveParams,
): Promise<API_DefectAnalytics> => {
  const { data } = await compliancesDefectAnalyticsRetrieve(requestParams);
  return parseApiResponse(api_defectAnalyticsSchema, data, 'DefectAnalytics');
};

export const useDefectAnalytics = (params: DefectAnalyticsFetcher, enabled: boolean) => {
  const requestParams = useMemo(() => buildDefectAnalyticsParams(params), [params]);

  // A null key disables the request entirely: this is a UK-only panel, so a US
  // user must not pay for a round-trip whose result is never rendered.
  const { data, error, isLoading } = useSWR(
    enabled ? [DEFECT_ANALYTICS_SWR_KEY, requestParams] : null,
    () => defectAnalyticsFetcher(requestParams),
    {
      keepPreviousData: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000, // 5 minutes, matching useCompliance
    },
  );

  return { data, isLoading, error };
};
